import type { QueryClient } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import { fetchBlockMap } from 'hooks/backend/blocks'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { capBlocksToLatestIndexedBlock } from 'hooks/useSubgraphLatestIndexedBlock'
import type { Pair as V2Pair, Token as V2Token } from 'queries/v2/generated'
import { useEffect, useMemo, useState } from 'react'
import { UnifiedPoolData } from 'types/analytics'
import { buildSelectionKey, dedupeIds, toNumber } from 'utils/analytics'
import { calculateAPRPercent } from 'utils/analytics/calculateAPR'
import { get2DayChange, getDelta, getPercentChange } from 'utils/analytics/data'
import { useDeltaTimestamps } from 'utils/queries'

import type {
  GetV2PoolsByIdsQuery,
  GetV2PoolsByIdsQueryVariables,
  GetV2PoolsByTokenQuery,
  GetV2PoolsByTokenQueryVariables,
  GetV2TopPoolsQuery,
  GetV2TopPoolsQueryVariables,
} from '../../queries/analytics/v3/generated'
import {
  useGetV2PoolsByIdsQuery,
  useGetV2PoolsByTokenQuery,
  useGetV2PoolsSnapshotsForTableQuery,
  useGetV2PoolsSnapshotsQuery,
  useGetV2TopPoolsQuery,
} from '../../queries/analytics/v3/generated'

type V2PoolToken = Pick<V2Token, 'id' | 'symbol' | 'name' | 'decimals'>

export type V2PoolData = Pick<V2Pair, 'id' | 'reserve0' | 'reserve1' | 'reserveUSD' | 'volumeUSD' | 'totalSupply'> & {
  token0: V2PoolToken
  token1: V2PoolToken
}

interface V2PoolMetrics
  extends Pick<
    UnifiedPoolData,
    | 'volumeUSD'
    | 'volumeUSDChange'
    | 'volumeUSDWeek'
    | 'feesUSD'
    | 'feesUSDWeek'
    | 'tvlUSDChange'
    | 'feesUSDChange'
    | 'APR'
  > {
  metricsLoaded: boolean
  metrics24hLoaded: boolean
  metrics7dLoaded: boolean
}

type FetchStrategy = { type: 'top' } | { type: 'byIds'; poolIds: string[] } | { type: 'byToken'; tokenAddress: string }

// Query Key Constants
const v2PoolsBaseQueryKey = (chainId: number, selectionKey: string) =>
  ['analytics', 'v2Pools', chainId, 'base', selectionKey] as const

export const v2PoolsMetricsQueryKey = (
  chainId: number,
  selectionKey: string,
  t24: number,
  t48: number,
  tWeek: number,
  include48hSnapshot: boolean,
  latestIndexedBlockNumber?: number
) =>
  [
    'analytics',
    'v2Pools',
    chainId,
    'metrics',
    selectionKey,
    t24,
    t48,
    tWeek,
    include48hSnapshot ? 'with48h' : 'no48h',
    latestIndexedBlockNumber ?? 'latest',
  ] as const

export const v2PoolsQueryKey = (
  chainId: number,
  selectionKey: string,
  t24: number,
  t48: number,
  tWeek: number,
  include48hSnapshot: boolean
) =>
  [
    'analytics',
    'v2Pools',
    chainId,
    'full',
    selectionKey,
    t24,
    t48,
    tWeek,
    include48hSnapshot ? 'with48h' : 'no48h',
  ] as const

type GraphV2Pool =
  | GetV2TopPoolsQuery['pairs'][number]
  | GetV2PoolsByIdsQuery['pairs'][number]
  | GetV2PoolsByTokenQuery['as0'][number]
  | GetV2PoolsByTokenQuery['as1'][number]

const mapGraphPairToPoolData = (pair: GraphV2Pool): V2PoolData => ({
  id: pair.id,
  reserve0: pair.reserve0 ?? '0',
  reserve1: pair.reserve1 ?? '0',
  reserveUSD: pair.reserveUSD ?? '0',
  volumeUSD: pair.volumeUSD ?? '0',
  totalSupply: pair.totalSupply ?? '0',
  token0: {
    id: pair.token0.id,
    symbol: pair.token0.symbol,
    name: pair.token0.name,
    decimals: pair.token0.decimals,
  },
  token1: {
    id: pair.token1.id,
    symbol: pair.token1.symbol,
    name: pair.token1.name,
    decimals: pair.token1.decimals,
  },
})

// Utility Functions
const mapPairsById = (pairs?: V2PoolData[]) =>
  pairs?.reduce<Record<string, V2PoolData>>((acc, pair) => {
    acc[pair.id.toLowerCase()] = pair
    return acc
  }, {}) ?? {}

export const dedupeV2Ids = (ids?: string[]) => dedupeIds(ids)

export const buildV2SelectionKey = (normalizedPoolIds?: string[], tokenAddress?: string) => {
  if (tokenAddress) return `token-${tokenAddress.toLowerCase()}`
  return buildSelectionKey(normalizedPoolIds)
}

const computeV2PoolMetrics = (
  current: V2PoolData | undefined,
  oneDay: V2PoolData | undefined,
  twoDays: V2PoolData | undefined,
  week: V2PoolData | undefined,
  resolved: { day: boolean; week: boolean },
  previous?: V2PoolMetrics
): V2PoolMetrics => {
  const [volumeUSDRaw, volumeUSDChangeRaw] =
    current && oneDay && twoDays
      ? get2DayChange(current.volumeUSD, oneDay.volumeUSD, twoDays.volumeUSD)
      : current && oneDay
      ? [getDelta(current.volumeUSD, oneDay.volumeUSD), 0]
      : [0, 0]
  const volumeUSD = Number.isFinite(volumeUSDRaw) ? Math.max(volumeUSDRaw, 0) : 0
  const hasWeeklySnapshot = resolved.week && week !== undefined
  const volumeUSDWeek =
    hasWeeklySnapshot && current ? getDelta(current.volumeUSD, week.volumeUSD) : previous?.volumeUSDWeek ?? 0
  const feesUSD = volumeUSD * 0.003
  const feesUSDWeek = hasWeeklySnapshot ? volumeUSDWeek * 0.003 : previous?.feesUSDWeek ?? 0
  const tvlUSDChange = getPercentChange(current?.reserveUSD, oneDay?.reserveUSD)

  const volumeUSDChange = Number.isFinite(volumeUSDChangeRaw) ? volumeUSDChangeRaw : 0

  const feesUSDChange = getPercentChange(
    (+(current?.volumeUSD ?? 0) * 0.003).toString(),
    (+(oneDay?.volumeUSD ?? 0) * 0.003).toString()
  )

  const APR = calculateAPRPercent(feesUSD, Number(current?.reserveUSD))

  const metrics24hLoaded = resolved.day
  const metrics7dLoaded = (resolved.week && current !== undefined) || previous?.metrics7dLoaded || false

  return {
    volumeUSD,
    volumeUSDWeek,
    feesUSD,
    feesUSDWeek,
    tvlUSDChange,
    volumeUSDChange,
    feesUSDChange,
    metricsLoaded: metrics24hLoaded && metrics7dLoaded,
    metrics24hLoaded,
    metrics7dLoaded,
    APR,
  }
}

const createV2PoolData = (pool: V2PoolData, overrides?: Partial<UnifiedPoolData>): UnifiedPoolData => {
  const base: UnifiedPoolData = {
    id: pool.id,
    address: pool.id,
    feeTier: 3000,
    token0: {
      id: pool.token0.id,
      address: pool.token0.id,
      symbol: pool.token0.symbol,
      name: pool.token0.name,
      decimals: Number(pool.token0.decimals),
      derivedETH: 0,
    },
    token1: {
      id: pool.token1.id,
      address: pool.token1.id,
      symbol: pool.token1.symbol,
      name: pool.token1.name,
      decimals: Number(pool.token1.decimals),
      derivedETH: 0,
    },
    tvlUSD: toNumber(pool.reserveUSD),
    tvlToken0: toNumber(pool.reserve0),
    tvlToken1: toNumber(pool.reserve1),
    volumeUSD: 0,
    feesUSD: 0,
    volumeUSDWeek: 0,
    feesUSDWeek: 0,
    metricsLoaded: false,
    metrics24hLoaded: false,
    metrics7dLoaded: false,
  }

  return overrides ? { ...base, ...overrides } : base
}

// Fetcher Functions
const fetchPoolsByStrategy = async (
  _chainId: number,
  endpoint: string,
  strategy: FetchStrategy,
  blockNumber?: number
): Promise<V2PoolData[]> => {
  const blockInput = blockNumber !== undefined ? { number: blockNumber } : undefined

  switch (strategy.type) {
    case 'top': {
      const variables: GetV2TopPoolsQueryVariables | undefined = blockInput ? { block: blockInput } : undefined
      const response = await useGetV2TopPoolsQuery.fetcher({ endpoint }, variables)()

      return (response.pairs ?? []).map(mapGraphPairToPoolData)
    }
    case 'byIds': {
      if (strategy.poolIds.length === 0) return []

      const variables: GetV2PoolsByIdsQueryVariables = {
        ids: strategy.poolIds,
        ...(blockInput ? { block: blockInput } : {}),
      }

      const response = await useGetV2PoolsByIdsQuery.fetcher({ endpoint }, variables)()

      return (response.pairs ?? []).map(mapGraphPairToPoolData)
    }
    case 'byToken': {
      const variables: GetV2PoolsByTokenQueryVariables = {
        tokens: [strategy.tokenAddress],
        ...(blockInput ? { block: blockInput } : {}),
      }

      const response = await useGetV2PoolsByTokenQuery.fetcher({ endpoint }, variables)()

      const pairs = [...(response.as0 ?? []), ...(response.as1 ?? [])]
      return pairs.map(mapGraphPairToPoolData)
    }
  }
}

type FetchV2PoolMetricsOptions = {
  providedBlocks?: Map<number, number>
  previousMetrics?: Record<string, V2PoolMetrics>
  include48hSnapshot?: boolean
  latestIndexedBlockNumber?: number
}

type SharedMetricsOptions = {
  blocks?: Map<number, number>
  timestamps?: [number, number, number]
  blocksRequired?: boolean
  include48hSnapshot?: boolean
  latestIndexedBlockNumber?: number
}

const fetchPoolMetrics = async (
  chainId: number,
  endpoint: string,
  currentPools: V2PoolData[],
  strategy: FetchStrategy,
  t24: number,
  t48: number,
  tWeek: number,
  options?: FetchV2PoolMetricsOptions
): Promise<Record<string, V2PoolMetrics>> => {
  if (!currentPools.length) return {}

  const { providedBlocks, previousMetrics, include48hSnapshot = true, latestIndexedBlockNumber } = options ?? {}

  const timestampsForBlocks = include48hSnapshot ? [t24, t48, tWeek] : [t24, tWeek]
  const uncappedBlocks = providedBlocks ?? (await fetchBlockMap({ chainId, timestamps: timestampsForBlocks }))
  const blocks = capBlocksToLatestIndexedBlock(uncappedBlocks, latestIndexedBlockNumber)
  const block24 = blocks.get(t24)
  const block48 = include48hSnapshot ? blocks.get(t48) : undefined
  const blockWeek = blocks.get(tWeek)

  let pairs24: V2PoolData[] = []
  let pairs48: V2PoolData[] = []
  let pairsWeek: V2PoolData[] | undefined

  const poolIdsForMetrics = Array.from(
    currentPools.reduce<Set<string>>((acc, pool) => {
      if (pool.id) {
        acc.add(pool.id)
      }
      return acc
    }, new Set<string>())
  )

  if (poolIdsForMetrics.length > 0 && (block24 !== undefined || blockWeek !== undefined)) {
    if (include48hSnapshot) {
      const variables = {
        ids: poolIdsForMetrics,
        block24: block24 ? { number: block24 } : undefined,
        block48: block48 ? { number: block48 } : undefined,
        blockWeek: blockWeek ? { number: blockWeek } : undefined,
      }

      const snapshots = await useGetV2PoolsSnapshotsQuery.fetcher({ endpoint }, variables)()

      if (block24 !== undefined) {
        pairs24 = (snapshots.block24 ?? []).map(mapGraphPairToPoolData)
      }

      if (block48 !== undefined) {
        pairs48 = (snapshots.block48 ?? []).map(mapGraphPairToPoolData)
      }

      if (blockWeek !== undefined) {
        pairsWeek = snapshots.blockWeek ? snapshots.blockWeek.map(mapGraphPairToPoolData) : undefined
      }
    } else {
      const variables = {
        ids: poolIdsForMetrics,
        block24: block24 ? { number: block24 } : undefined,
        blockWeek: blockWeek ? { number: blockWeek } : undefined,
      }

      const snapshots = await useGetV2PoolsSnapshotsForTableQuery.fetcher({ endpoint }, variables)()

      if (block24 !== undefined) {
        pairs24 = (snapshots.block24 ?? []).map(mapGraphPairToPoolData)
      }

      if (blockWeek !== undefined) {
        pairsWeek = snapshots.blockWeek ? snapshots.blockWeek.map(mapGraphPairToPoolData) : undefined
      }

      pairs48 = []
    }
  }

  const currentById = mapPairsById(currentPools)
  const dayById = mapPairsById(pairs24)
  const twoDaysById = mapPairsById(pairs48)
  const dayResolved = true

  const computeMetrics = (
    weekById?: Record<string, V2PoolData>,
    weekResolved = false,
    previousOverride?: Record<string, V2PoolMetrics>
  ) =>
    currentPools.reduce<Record<string, V2PoolMetrics>>((acc, pool) => {
      const poolId = pool.id.toLowerCase()
      acc[poolId] = computeV2PoolMetrics(
        currentById[poolId],
        dayById[poolId],
        twoDaysById[poolId],
        weekById?.[poolId],
        {
          day: dayResolved,
          week: weekResolved,
        },
        previousOverride?.[poolId] ?? previousMetrics?.[poolId]
      )
      return acc
    }, {})

  const weekById = pairsWeek ? mapPairsById(pairsWeek) : undefined
  const weekResolved = Boolean(blockWeek && pairsWeek)
  const metrics = computeMetrics(weekById, weekResolved, previousMetrics)

  return metrics
}

export const prefetchV2TopPools = async (
  queryClient: QueryClient,
  chainId: number,
  t24: number,
  t48: number,
  tWeek: number,
  blocks?: Map<number, number>,
  include48hSnapshot = true,
  latestIndexedBlockNumber?: number
) => {
  let endpoint: string
  try {
    endpoint = getSubgraphConfig(chainId, SubgraphType.V3).endpoint
  } catch (error) {
    console.error(error)
    return
  }

  const selectionKey = buildV2SelectionKey(undefined)
  const strategy: FetchStrategy = { type: 'top' }

  const basePools =
    (await queryClient.ensureQueryData({
      queryKey: v2PoolsBaseQueryKey(chainId, selectionKey),
      queryFn: () => fetchPoolsByStrategy(chainId, endpoint, strategy),
      staleTime: 1000 * 60 * 5, // 5 minutes
    })) ?? []

  await queryClient.prefetchQuery({
    queryKey: v2PoolsMetricsQueryKey(
      chainId,
      selectionKey,
      t24,
      t48,
      tWeek,
      include48hSnapshot,
      latestIndexedBlockNumber
    ),
    queryFn: () =>
      fetchPoolMetrics(chainId, endpoint, basePools, strategy, t24, t48, tWeek, {
        providedBlocks: blocks,
        include48hSnapshot,
        latestIndexedBlockNumber,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Unified Hook Core Logic
const useV2PoolsCore = (
  strategy: FetchStrategy,
  selectionKey: string,
  enabled: boolean,
  shared?: SharedMetricsOptions
) => {
  const defaultTimestamps = useDeltaTimestamps()
  const [t24, t48, tWeek] = shared?.timestamps ?? defaultTimestamps
  const providedBlocks = shared?.blocks
  const include48hSnapshot = shared?.include48hSnapshot ?? true
  const latestIndexedBlockNumber = shared?.latestIndexedBlockNumber
  const { chainId } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const endpoint = subgraphConfig.endpoint
  const queryClient = useQueryClient()
  const [hasRefetchedEmptyBase, setHasRefetchedEmptyBase] = useState(false)

  const isTopStrategy = strategy.type === 'top'
  const isByIdsStrategy = strategy.type === 'byIds'
  const poolIds = strategy.type === 'byIds' ? strategy.poolIds : []
  const tokenAddress = strategy.type === 'byToken' ? strategy.tokenAddress : ''
  const baseQueryKey = useMemo(() => v2PoolsBaseQueryKey(chainId, selectionKey), [chainId, selectionKey])

  const topPoolsQuery = useGetV2TopPoolsQuery<GetV2TopPoolsQuery>(
    {
      endpoint,
    },
    undefined,
    {
      enabled: enabled && isTopStrategy,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2TopPoolsQuery) => response,
    }
  )

  const poolsByIdsQuery = useGetV2PoolsByIdsQuery<GetV2PoolsByIdsQuery>(
    {
      endpoint,
    },
    {
      ids: poolIds,
    },
    {
      enabled: enabled && isByIdsStrategy && poolIds.length > 0,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2PoolsByIdsQuery) => response,
    }
  )

  const poolsByTokenQuery = useGetV2PoolsByTokenQuery<GetV2PoolsByTokenQuery>(
    {
      endpoint,
    },
    {
      tokens: [tokenAddress],
    },
    {
      enabled: enabled && strategy.type === 'byToken',
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2PoolsByTokenQuery) => response,
    }
  )

  const rawTopPools = useMemo(() => topPoolsQuery.data?.pairs, [topPoolsQuery.data?.pairs])
  const rawPoolsByIds = useMemo(() => poolsByIdsQuery.data?.pairs, [poolsByIdsQuery.data?.pairs])
  const rawPoolsByToken = useMemo(() => {
    const as0 = poolsByTokenQuery.data?.as0
    const as1 = poolsByTokenQuery.data?.as1
    if (!as0 && !as1) return undefined
    return [...(as0 ?? []), ...(as1 ?? [])]
  }, [poolsByTokenQuery.data?.as0, poolsByTokenQuery.data?.as1])

  const baseQuery =
    strategy.type === 'top' ? topPoolsQuery : strategy.type === 'byIds' ? poolsByIdsQuery : poolsByTokenQuery
  const { isFetched: isBaseQueryFetched, isFetching: isBaseQueryFetching, refetch: refetchBaseQuery } = baseQuery

  const basePoolNodes = useMemo<GraphV2Pool[] | undefined>(() => {
    switch (strategy.type) {
      case 'top':
        return rawTopPools
      case 'byIds':
        return rawPoolsByIds
      case 'byToken':
        return rawPoolsByToken
    }
  }, [strategy.type, rawTopPools, rawPoolsByIds, rawPoolsByToken])

  const baseData = useMemo(
    () => (basePoolNodes ? basePoolNodes.map(mapGraphPairToPoolData) : undefined),
    [basePoolNodes]
  )

  const baseDataLength = baseData?.length ?? 0
  const deferEmptyResult = (!baseData && isBaseQueryFetching) || (baseDataLength === 0 && isBaseQueryFetching)

  useEffect(() => {
    if (!enabled) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (!isBaseQueryFetched || isBaseQueryFetching) return

    if (baseDataLength > 0) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (hasRefetchedEmptyBase) return

    setHasRefetchedEmptyBase(true)
    void refetchBaseQuery()
  }, [enabled, baseDataLength, hasRefetchedEmptyBase, isBaseQueryFetched, isBaseQueryFetching, refetchBaseQuery])

  useEffect(() => {
    setHasRefetchedEmptyBase(false)
  }, [selectionKey])

  const metricsQueryKey = useMemo(
    () => v2PoolsMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, include48hSnapshot, latestIndexedBlockNumber),
    [chainId, selectionKey, t24, t48, tWeek, include48hSnapshot, latestIndexedBlockNumber]
  )

  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      const previousMetrics = queryClient.getQueryData<Record<string, V2PoolMetrics>>(metricsQueryKey)
      return fetchPoolMetrics(chainId, endpoint, baseData ?? [], strategy, t24, t48, tWeek, {
        providedBlocks,
        previousMetrics,
        include48hSnapshot,
        latestIndexedBlockNumber,
      })
    },
    enabled: enabled && baseDataLength > 0 && (shared?.blocksRequired ?? true),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const metricsData = metricsQuery.data

  const basePools = useMemo(() => {
    if (deferEmptyResult || !baseData) return undefined
    return baseData.map((pool) => createV2PoolData(pool))
  }, [baseData, deferEmptyResult])

  const combinedData = useMemo(() => {
    if (!basePools || !metricsData) return undefined
    const metricsById = metricsData

    return basePools.map((pool) => {
      const metrics = metricsById[pool.id.toLowerCase()]
      return metrics ? { ...pool, ...metrics } : pool
    })
  }, [basePools, metricsData])

  const refetch = async () => {
    const baseResult = await baseQuery.refetch({ cancelRefetch: false })
    if (!metricsQuery.isPaused) {
      await metricsQuery.refetch({ cancelRefetch: false })
    }
    return baseResult
  }

  const hasCombinedData = combinedData !== undefined

  return {
    data: combinedData,
    isLoading: (baseQuery.isLoading || metricsQuery.isLoading) && !hasCombinedData,
    isFetching: baseQuery.isFetching || metricsQuery.isFetching,
    error: baseQuery.error ?? metricsQuery.error,
    refetch,
  }
}

// Public Hooks
type UseV2PoolDataOptions = SharedMetricsOptions & {
  enabled?: boolean
}

export const useV2PoolDatas = (poolIds?: string[], options?: UseV2PoolDataOptions) => {
  const normalizedPoolIds = useMemo(() => dedupeV2Ids(poolIds), [poolIds])
  const selectionKey = useMemo(() => buildV2SelectionKey(normalizedPoolIds), [normalizedPoolIds])

  const strategy: FetchStrategy = useMemo(
    () => (normalizedPoolIds ? { type: 'byIds', poolIds: normalizedPoolIds } : { type: 'top' }),
    [normalizedPoolIds]
  )

  const enabled = (options?.enabled ?? true) && (normalizedPoolIds === undefined || normalizedPoolIds.length > 0)

  return useV2PoolsCore(strategy, selectionKey, enabled, options)
}

export const useV2PoolsByToken = (address: string, options?: UseV2PoolDataOptions) => {
  const selectionKey = buildV2SelectionKey(undefined, address)
  const strategy: FetchStrategy = { type: 'byToken', tokenAddress: address }

  return useV2PoolsCore(strategy, selectionKey, options?.enabled ?? true, options)
}
